import { useEffect, useState, useRef } from 'react';
import { Box, Typography, CircularProgress, Alert, TextField } from '@mui/material';
import { Form } from 'antd';
import CheckboxF from '../../ChexBox';
import axios from 'axios';
import { Crypter } from '../../../Functions/Security/Crypter';
import { useAuthUser } from '../../../Services/Auth/sessionAuth';
import Utilisateur from '../../../Models/Utilisateur';
import Data from '../../../Data/Data.json';

interface DetailFormat {
  IdDetail?: number;
  IdFormat?: number;
  NomGroupe?: string;
  Longueur?: number;
  PositionDepart?: number;
  OrdreAffichage?: number;
  SeparateurAvant?: string;
  TypeSegment?: string;
  CodFormat?: string;
  NOMGROUPE?: string;
  LONGUEUR?: number;
  ORDREAFFICHAGE?: number;
  SEPARATEURAVANT?: string;
  CODFORMAT?: string;
}

interface CleLibreInputProps {
  form?: any;
  authHeader: () => string;
  disabled?: boolean;
}

const MASK = '_';

function parseAnySearchResponse(decryptedText: string): any[] {
  try {
    const parsed = JSON.parse(decryptedText);
    const value = parsed?.Value ?? parsed;
    const result = value?.Result ?? value;
    return result?.Table ?? result ?? [];
  } catch {
    return [];
  }
}

function buildSearchPayload(table: string, colonne1: string, colonne2: string, init = '', options?: { colfiltre?: string; valfiltre?: string; colagence?: string; codeAgence?: string; choix?: number }) {
  return {
    CodeAgence: options?.codeAgence ?? '',
    Table: table,
    Colonne1: colonne2,
    Colonne2: colonne1,
    Colagence: options?.colagence ?? '',
    Init: init,
    choix: options?.choix ?? 22,
    colfiltre: options?.colfiltre ?? '',
    valfiltre: options?.valfiltre ?? '',
  };
}

function normalizeName(seg: DetailFormat): string {
  return (seg.NomGroupe ?? seg.NOMGROUPE ?? '').trim();
}

function normalizeLongueur(seg: DetailFormat): number {
  return Number(seg.Longueur ?? seg.LONGUEUR ?? 0);
}

function normalizeSeparateur(seg: DetailFormat): string {
  return (seg.SeparateurAvant ?? seg.SEPARATEURAVANT ?? '').trim();
}

// Préfixe ignoré en tête du numéro client (le numéro réel démarre au 3ème caractère)
const GL_PREFIX = 'GL';

export default function CleLibreInput({ form, authHeader, disabled = false }: CleLibreInputProps) {
  const auth = useAuthUser();
  const user: Utilisateur = JSON.parse(JSON.stringify(auth()));

  const [ordreLibre, setOrdreLibre] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [segments, setSegments] = useState<DetailFormat[]>([]);
  const [values, setValues] = useState<Record<string, string>>({});
  const [planCp, setPlanCp] = useState<any>(null);
  const inputRefs = useRef<HTMLInputElement[]>([]);

  // Plan comptable et client sélectionnés dans le formulaire parent
  const idPlan = Form.useWatch('IDPLAN', form);
  const numCli = Form.useWatch('NUMCLI', form);
  const agcCpt = Form.useWatch('AGCCPT', form);

  useEffect(() => {
    // Initialiser explicitement NumeroOrdre dans le formulaire pour qu'il soit envoyé au submit
    const existing = form?.getFieldValue('NumeroOrdre');
    form?.setFieldValue('NumeroOrdre', existing === undefined || existing === null ? '' : String(existing));

    const loadFormat = async () => {
      setLoading(true);
      setError(null);
      try {
        const societePayload = buildSearchPayload('SOCIETE', 'FORMATCPT', 'CODSTE', '', {
          colfiltre: 'CODSTE',
          valfiltre: '10001',
        });
        const societeRes = await axios.get(Data.ApiUrl.AnySearch, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': authHeader(),
            'CypherSearch': Crypter.encrypt(JSON.stringify(societePayload)),
          },
        });
        const societeList = parseAnySearchResponse(Crypter.decrypt(societeRes.data));
        const societe = Array.isArray(societeList) && societeList.length > 0 ? societeList[0] : societeList;

        if (!societe) {
          setError('Aucune société trouvée.');
          setLoading(false);
          return;
        }

        let formatGlId: number | null = null;
        const formatField = societe.FORMATCPT || societe.formatCptGl || societe.formatcptGl || societe.FormatCpt;
        if (typeof formatField === 'string' && formatField.trim().startsWith('{')) {
          try {
            const parsed = JSON.parse(formatField);
            formatGlId = Number(parsed.formatcptGl || parsed.formatCptGl || parsed.FormatcptGl);
          } catch {
            // ignore
          }
        }
        if (!formatGlId) formatGlId = Number(formatField);

        if (!formatGlId) {
          setError('Format de compte GL non défini dans la société.');
          setLoading(false);
          return;
        }

        const detailPayload = buildSearchPayload('DETAILFORMATCOMPTE', 'IDFORMAT', 'NOMGROUPE', String(formatGlId), {
          colfiltre: 'IDFORMAT',
          valfiltre: String(formatGlId),
          choix: 37
        });
        const detailRes = await axios.get(Data.ApiUrl.AnySearch, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': authHeader(),
            'CypherSearch': Crypter.encrypt(JSON.stringify(detailPayload)),
          },
        });
        const detailList = parseAnySearchResponse(Crypter.decrypt(detailRes.data));
        const details: DetailFormat[] = Array.isArray(detailList) ? detailList : [];
        const sortedDetails = details.sort(
          (a, b) => ((a.OrdreAffichage ?? a.ORDREAFFICHAGE ?? 0) - (b.OrdreAffichage ?? b.ORDREAFFICHAGE ?? 0))
        );
        setSegments(sortedDetails);
      } catch (err: any) {
        setError(err.message || 'Erreur lors du chargement du format de compte.');
      } finally {
        setLoading(false);
      }
    };

    loadFormat();
  }, [authHeader, form]);

  // Récupère les éléments du plan comptable sélectionné (codage, codprod...)
  useEffect(() => {
    if (!idPlan) {
      setPlanCp(null);
      return;
    }
    const loadPlanCp = async () => {
      try {
        const response = await axios.get(Data.ApiUrl.ReadPlanCp2, {
          headers: {
            'CypherIdPLan': String(idPlan),
            'CypherCodeage': user.codage,
            'Authorization': authHeader(),
            'Content-Type': 'application/json',
          },
        });
        if (response.data) {
          const res = JSON.parse(Crypter.decrypt(response.data));
          if (res.StatutCode === 200 && res.Result?.Table?.length > 0) {
            setPlanCp(res.Result.Table[0]);
            return;
          }
        }
        setPlanCp(null);
      } catch {
        setPlanCp(null);
      }
    };
    loadPlanCp();
  }, [idPlan, authHeader, user.codage]);

  // Preview du numéro de compte futur : remplit les cases grisées
  useEffect(() => {
    if (!segments.length) return;
    setValues((prev) => {
      const next: Record<string, string> = { ...prev };
      segments.forEach((seg) => {
        const name = normalizeName(seg).toLowerCase();
        const len = normalizeLongueur(seg);
        if (name.includes('ordre')) {
          // Segment libre : conserve la saisie de l'utilisateur
          if (next[name] === undefined) next[name] = ''.padEnd(len, MASK);
          return;
        }
        let raw = '';
        if (name.includes('codage')) {
          raw = String(planCp?.codage ?? planCp?.CODAGE ?? agcCpt ?? user.codage ?? '');
        } else if (name.includes('produit')) {
          raw = String(planCp?.codprod ?? planCp?.CODPROD ?? '');
        } else if (name.includes('client')) {
          raw = String(numCli ?? '');
          // Le préfixe "GL" du numéro client n'est pas repris : on démarre au 3ème caractère
          if (raw.toUpperCase().startsWith(GL_PREFIX)) {
            raw = raw.slice(GL_PREFIX.length);
          }
        }
        // La clé de compte reste masquée : elle est calculée par le backend
        next[name] = raw.slice(0, len).padStart(raw ? len : 0, '0').padEnd(len, MASK);
      });
      return next;
    });
  }, [segments, planCp, numCli, agcCpt, user.codage]);

  const handleCharChange = (segName: string, index: number, char: string, length: number, refIndex: number) => {
    if (char && !/^\d*$/.test(char)) return;
    setValues((prev) => {
      const current = (prev[segName] || '').padEnd(length, MASK);
      const arr = current.split('');
      arr[index] = char || MASK;
      const nextVal = arr.join('');
      const next = { ...prev, [segName]: nextVal };

      if (segName.includes('ordre')) {
        form?.setFieldValue('NumeroOrdre', nextVal.replaceAll(MASK, ''));
      }
      return next;
    });

    if (char && index < length - 1) {
      inputRefs.current[refIndex + 1]?.focus();
    }
  };

  let globalIndex = 0;

  return (
    <Box
      sx={{
        p: 2,
        mt: 1,
        mb: 1,
        border: '1px solid #116599',
        borderRadius: 2,
        bgcolor: disabled ? '#f5f5f5' : '#fafafa',
        opacity: disabled ? 0.6 : 1,
        pointerEvents: disabled ? 'none' : 'auto',
      }}
    >
      {loading && <CircularProgress size={20} color='success' sx={{ mr: 1 }} />}
      {error && <Alert severity='info' sx={{ mb: 1, fontSize: 12 }}>{error}</Alert>}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: ordreLibre ? 2 : 0 }}>
        <CheckboxF
          name='isOrdreLibre'
          label="Numéro d'ordre libre"
          onChange={(e: any) => {
            const checked = e.target.checked;
            setOrdreLibre(checked);
            if (!checked) {
              form?.setFieldValue('NumeroOrdre', '');
              setValues((prev) => {
                const ordreSeg = segments.find(s => normalizeName(s).toLowerCase().includes('ordre'));
                if (ordreSeg) {
                  const name = normalizeName(ordreSeg).toLowerCase();
                  const len = normalizeLongueur(ordreSeg);
                  return { ...prev, [name]: ''.padEnd(len, MASK) };
                }
                return prev;
              });
            }
          }}
        />
        <Typography variant='caption' color='text.secondary'>
          {ordreLibre ? "Saisissez le numéro d'ordre manuellement." : "Le numéro d'ordre sera généré automatiquement."}
        </Typography>
      </Box>

      {ordreLibre && (
        <>
          <Typography variant='subtitle2' color='#116599' sx={{ mt: 1.5, mb: 1.5, fontWeight: 600 }}>
            Numéro de compte GL
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5, mb: 2 }}>
            {segments.map((seg, segIdx) => {
              const name = normalizeName(seg);
              const nameLower = name.toLowerCase();
              const len = normalizeLongueur(seg);
              const sep = normalizeSeparateur(seg);
              const editable = nameLower.includes('ordre');
              const current = (values[nameLower] || '').padEnd(len, MASK);

              return (
                <Box key={name} sx={{ display: 'flex', alignItems: 'center' }}>
                  {segIdx > 0 && sep && (
                    <Typography sx={{ mx: 0.5, color: '#116599', fontWeight: 700 }}>{sep}</Typography>
                  )}
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', gap: 0.3 }}>
                      {Array.from({ length: len }).map((_, i) => {
                        const refIdx = globalIndex++;
                        return (
                          <TextField
                            key={i}
                            inputRef={(el) => (inputRefs.current[refIdx] = el)}
                            value={current[i] === MASK ? '' : current[i]}
                            placeholder={MASK}
                            disabled={disabled || !editable}
                            inputProps={{
                              maxLength: 1,
                              style: {
                                textAlign: 'center',
                                fontWeight: 700,
                                fontSize: 16,
                                padding: '6px 0',
                                width: 22,
                              }
                            }}
                            sx={{
                              width: 36,
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 1,
                                backgroundColor: (disabled || !editable) ? '#e8e8e8' : '#fff',
                              },
                              '& input::placeholder': {
                                color: '#bbb',
                                opacity: 1,
                              }
                            }}
                            onChange={(e) => handleCharChange(nameLower, i, e.target.value, len, refIdx)}
                            onFocus={(e) => e.target.select()}
                          />
                        );
                      })}
                    </Box>
                    <Typography variant='caption' color='text.secondary' sx={{ mt: 0.5, fontSize: 10 }}>
                      {name}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </>
      )}
    </Box>
  );
}
